import React, { useState, useCallback } from 'react';
import { Container, Card, Button, Modal, Spinner } from 'react-bootstrap';
import { FiEye, FiTrash2, FiUser, FiAlertTriangle } from 'react-icons/fi';
import TableActionButton from '@core/components/TableActionButton';
import TableActions from '@core/components/TableActions';
import { SearchBar, FilterField } from '@core/components/SearchBar';
import { useSearch, SearchFetchFn, SearchPage } from '@core/hooks/useSearch';
import {
  fetchEntityUserRoleAssignments,
  removeEntityUser,
} from '@features/user/api/userApi';
import { DataTable, DataTableColumn } from '@core/components/DataTable';
import { EntityUserRoleAssignment } from '@features/user/types/user';
import UserDetailsContactFields from '@features/user/components/UserDetailsContactFields';
import ApiResponse from '@core/types/apiResponse';
import { showError } from '@infrastructure/api/utils/toastNotifications';
import PaginationBar from '@core/components/PaginationBar';
import TablePaginationFooter, { getPaginationRange } from '@core/components/TablePaginationFooter';
import {
  SORT_BY_EMAIL,
  SORT_BY_PERSON_NAME,
  SORT_BY_ROLE,
  SORT_DIR_ASC,
  SORT_DIR_DESC,
  filtersWithoutSort,
  normalizeSortDir,
} from '@core/constants/listSort';
import { usePermissions } from '@features/entity/hooks/usePermissions';
import { getRemoveUserActionForScope } from '@features/user/constants/removeUserActions';

const ASSIGNMENT_SORT_OPTIONS = [
  { value: `${SORT_BY_PERSON_NAME}:${SORT_DIR_ASC}`, label: 'Name A–Z' },
  { value: `${SORT_BY_PERSON_NAME}:${SORT_DIR_DESC}`, label: 'Name Z–A' },
  { value: `${SORT_BY_EMAIL}:${SORT_DIR_ASC}`, label: 'Email A–Z' },
  { value: `${SORT_BY_EMAIL}:${SORT_DIR_DESC}`, label: 'Email Z–A' },
  { value: `${SORT_BY_ROLE}:${SORT_DIR_ASC}`, label: 'Role A–Z' },
  { value: `${SORT_BY_ROLE}:${SORT_DIR_DESC}`, label: 'Role Z–A' },
];

const ASSIGNMENT_FILTER_FIELDS: FilterField[] = [
  { name: 'firstName', label: 'First Name', type: 'text', placeholder: 'Filter by first name', colWidth: 4 },
  { name: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Filter by last name', colWidth: 4 },
  { name: 'email', label: 'Email', type: 'text', placeholder: 'Filter by email', colWidth: 4 },
  { name: 'profileDescription', label: 'Role', type: 'text', placeholder: 'Filter by role', colWidth: 4 },
  { name: 'departmentName', label: 'Department', type: 'text', placeholder: 'Filter by department name', colWidth: 4 },
  { name: 'trn', label: 'TRN', type: 'text', placeholder: 'Filter by TRN', colWidth: 4 },
  {
    name: 'assignmentSort',
    label: 'Sort',
    type: 'select',
    includeEmptyOption: false,
    colWidth: 4,
    options: ASSIGNMENT_SORT_OPTIONS,
  },
];

export interface EntityUserRoleAssignmentsPageProps {
  scope: 'NATION' | 'DISTRICT' | 'CHURCH';
  scopeRefId: number;
  title: string;
  subtitle: string;
  /** Display name of the selected entity for remove confirm copy (e.g. "Nation: Jamaica"). */
  selectedEntityDisplayName: string;
  /** Optional control rendered inline before the search input (e.g. entity scope picker). */
  leadingControl?: React.ReactNode;
  testId?: string;
}

function parseAssignmentSort(filters: Record<string, any>): {
  sortBy: string;
  sortDir: string;
} {
  const raw = String(filters.assignmentSort || `${SORT_BY_PERSON_NAME}:${SORT_DIR_ASC}`);
  const [sortBy, sortDir] = raw.split(':');
  const allowed = new Set([SORT_BY_PERSON_NAME, SORT_BY_EMAIL, SORT_BY_ROLE]);
  return {
    sortBy: allowed.has(sortBy) ? sortBy : SORT_BY_PERSON_NAME,
    sortDir: normalizeSortDir(sortDir),
  };
}

const EntityUserRoleAssignmentsPage: React.FC<EntityUserRoleAssignmentsPageProps> = ({
  scope,
  scopeRefId,
  title,
  subtitle,
  selectedEntityDisplayName,
  leadingControl,
  testId = 'entity-user-role-assignments-search',
}) => {
  const removeAction = getRemoveUserActionForScope(scope);
  const { hasPermission, loading: permissionLoading } = usePermissions({
    scopeType: scope,
    scopeId: scopeRefId,
    actionCodes: [removeAction],
  });
  // Hide Remove while access is loading or denied (same as RequirePermission mode="section").
  const canRemove = !permissionLoading && hasPermission(removeAction);
  const [selectedUser, setSelectedUser] = useState<EntityUserRoleAssignment | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState<EntityUserRoleAssignment | null>(null);

  const fetchFn = useCallback<SearchFetchFn<EntityUserRoleAssignment>>(
    ({ freetext, page, perPage, filters }) => {
      const { sortBy, sortDir } = parseAssignmentSort(filters);
      const contentFilters = filtersWithoutSort({ ...filters });
      delete contentFilters.assignmentSort;
      const filterEntries = Object.entries(contentFilters).filter(
        ([, v]) => v !== undefined && v !== null && v !== ''
      );
      const hasSearch = Boolean(freetext?.trim()) || filterEntries.length > 0;
      return fetchEntityUserRoleAssignments(scope, scopeRefId, {
        ...(freetext?.trim() ? { freetext: freetext.trim() } : {}),
        ...Object.fromEntries(filterEntries),
        ...(hasSearch ? {} : { all: 'true' }),
        sortBy,
        sortDir,
        page: String(page),
        perPage: String(perPage),
      }) as unknown as Promise<ApiResponse<SearchPage<EntityUserRoleAssignment>>>;
    },
    [scope, scopeRefId]
  );

  const search = useSearch<EntityUserRoleAssignment>({
    fetchFn,
    initialPerPage: 20,
    enabled: !!scopeRefId,
    syncUrl: true,
  });

  const activeFilterCount = Object.entries(search.filters).filter(
    ([key, value]) =>
      key !== 'assignmentSort' &&
      value !== undefined &&
      value !== null &&
      value !== ''
  ).length;

  const { start, end } = getPaginationRange({
    from: search.from,
    page: search.page,
    perPage: search.perPage,
    total: search.total,
    currentCount: search.data.length,
  });

  const handleView = (user: EntityUserRoleAssignment) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleRemoveClick = (user: EntityUserRoleAssignment) => {
    setUserToRemove(user);
    setShowRemoveModal(true);
  };

  const handleRemoveConfirm = async () => {
    if (!userToRemove) return;

    setRemoving(true);
    try {
      const response = await removeEntityUser(scope, scopeRefId, {
        uid: userToRemove.uid,
        role: userToRemove.profile_code,
        department_id: userToRemove.department_id,
      });

      if (response.isSuccess()) {
        setShowRemoveModal(false);
        setUserToRemove(null);
        search.reload();
      } else {
        const err = response.getError?.() || (response as any).error;
        showError(err?.detail || 'Failed to remove role assignment');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove role assignment');
    } finally {
      setRemoving(false);
    }
  };

  const columns: DataTableColumn<EntityUserRoleAssignment>[] = [
    {
      key: 'name',
      label: 'Name',
      role: 'title',
      cardRender: (_value: any, row: EntityUserRoleAssignment) => `${row.fname} ${row.lname}`,
      render: (_value: any, row: EntityUserRoleAssignment) => (
        <div className="fw-medium">
          {row.fname} {row.lname}
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      cardLabel: '',
      cardRender: (_value: any, row: EntityUserRoleAssignment) => row.email || null,
      render: (_value: any, row: EntityUserRoleAssignment) => (
        <span className="text-muted">{row.email}</span>
      ),
    },
    {
      key: 'profile_description',
      label: 'Role',
      cardRender: (_value: any, row: EntityUserRoleAssignment) =>
        `${row.profile_description || 'No role assigned'}${
          row.department_name ? ` (${row.department_name})` : ''
        }`,
      render: (_value: any, row: EntityUserRoleAssignment) => (
        <div>
          <small>{row.profile_description || 'No role assigned'}</small>
          {row.department_name && (
            <small className="text-muted"> ({row.department_name})</small>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-end',
      role: 'actions',
      render: (_value: any, row: EntityUserRoleAssignment) => (
        <TableActions aria-label={`Actions for ${row.fname} ${row.lname}`}>
          <TableActionButton
            icon={<FiEye />}
            label="View"
            primary
            variant="outline-primary"
            onClick={() => handleView(row)}
          />
          {canRemove && (
            <TableActionButton
              icon={<FiTrash2 />}
              label="Remove"
              variant="outline-danger"
              onClick={() => handleRemoveClick(row)}
              disabled={removing}
            />
          )}
        </TableActions>
      ),
    },
  ];

  return (
    <Container fluid className="py-4">
      <div className="ntcog-page-title-block mb-4">
        <h1 className="ntcog-page-heading mb-1">{title}</h1>
        <p className="text-muted mb-0">{subtitle}</p>
      </div>

      <div className="ntcog-list-search-section">
        <SearchBar
          placeholder="Search by name, email, or role…"
          filterFields={ASSIGNMENT_FILTER_FIELDS}
          initialQuery={search.initialQuery}
          onQueryChange={search.setQuery}
          onFiltersApply={search.setFilters}
          onClear={search.reset}
          loading={search.loading}
          activeFilterCount={activeFilterCount}
          filtersIgnored={search.filtersIgnored}
          leadingControl={leadingControl}
          testId={testId}
        />
      </div>

      <Card className="shadow-sm ntcog-card ntcog-list-table-card">
        <Card.Body className="p-0">
          <DataTable
            data={search.data}
            columns={columns}
            rowNumberOffset={(search.from ?? 1) - 1}
            loading={search.loading}
            error={search.error}
            emptyMessage="No role assignments found"
            cardAriaLabel={(row) => `Assignment for ${row.fname} ${row.lname}`}
            striped
            hover
            responsive
          />
        </Card.Body>
      </Card>

      {search.lastPage > 1 && (
        <TablePaginationFooter
          className="mt-3"
          info={`Showing ${start} to ${end} of ${search.total} assignments`}
        >
          <PaginationBar
            page={search.page}
            totalPages={search.lastPage}
            onPage={search.setPage}
          />
        </TablePaginationFooter>
      )}

      <Modal
        show={showRemoveModal}
        onHide={() => !removing && setShowRemoveModal(false)}
        centered
        backdrop="static"
        keyboard={!removing}
      >
        <Modal.Header closeButton={!removing} className="border-0 pb-0">
          <Modal.Title className="fw-semibold">
            <FiAlertTriangle className="me-2 text-danger" aria-hidden />
            Confirm Removal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userToRemove && (
            <p>
              Are you sure you want to remove{' '}
              <strong>
                {userToRemove.fname} {userToRemove.lname}
              </strong>
              's "{userToRemove.profile_description}" role from{' '}
              <strong>{selectedEntityDisplayName}</strong>?
            </p>
          )}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="outline-secondary"
              onClick={() => setShowRemoveModal(false)}
              disabled={removing}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="btn-action"
              onClick={handleRemoveConfirm}
              disabled={removing}
              aria-busy={removing}
            >
              {removing ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" aria-hidden />
                  Removing…
                </>
              ) : (
                <>
                  <FiTrash2 className="me-2" aria-hidden />
                  Remove
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        centered
        scrollable
        dialogClassName="modal-dialog-scrollable"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-semibold">
            <FiUser className="me-2" aria-hidden />
            User Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div>
              <div className="mb-3">
                <div className="text-muted small">Name</div>
                <div className="fw-medium">
                  {selectedUser.fname} {selectedUser.lname}
                </div>
              </div>
              <div className="mb-3">
                <div className="text-muted small">Email</div>
                <div>{selectedUser.email}</div>
              </div>
              <div className="mb-3">
                <div className="text-muted small">TRN</div>
                <div>{selectedUser.trn || 'N/A'}</div>
              </div>
              <UserDetailsContactFields
                mobile={selectedUser.phone_mobile_no}
                home={selectedUser.phone_home_no}
                office={selectedUser.phone_office_no}
                mailingAddress={selectedUser.mailing_address}
                ministerId={selectedUser.minister_id}
              />
              <div className="mb-3">
                <div className="text-muted small">Role</div>
                <div>{selectedUser.profile_description}</div>
              </div>
              <div className="mb-3">
                <div className="text-muted small">Department</div>
                <div>{selectedUser.department_name}</div>
              </div>
              <div className="mb-3">
                <div className="text-muted small">Scope</div>
                <div>
                  {selectedUser.scope_ref_name} ({selectedUser.scope_type})
                </div>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="primary"
                  size="lg"
                  className="btn-action"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default EntityUserRoleAssignmentsPage;
